import React, { useState, useEffect, useMemo, MouseEventHandler } from 'react'
import Select, {
  components,
  MultiValueGenericProps,
  MultiValueProps,
} from 'react-select'
import {
  SortableContainer,
  SortableElement,
  SortEndHandler,
  SortableHandle,
} from 'react-sortable-hoc'

const styles = {
  control: (provided) => ({
    ...provided,
    minHeight: 36,
    height: 36,
    borderColor: 'var(--btn-border)',
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'border-color 0.3s',
    boxShadow: 'var(--btn-shadow)',

    '&:hover': {
      borderColor: 'var(--btn-border-darken)',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--text-light)',
  }),
  input: (provided) => ({
    ...provided,
    color: 'var(--text-primary)',
    fontWeight: 500,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--text-primary)',
    fontWeight: 500,
  }),
  multiValue: (provided) => ({
    ...provided,
    borderRadius: 4,
    background: 'var(--new-color-gray-light)',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    transition: 'background 0.3s',
    '&:hover': {
      color: 'white',
      background: 'var(--text-red)',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),

  menuList: (provided) => ({
    ...provided,
    padding: 6,
    paddingBottom: 0,
    maxHeight: 130,
    borderRadius: 6,
  }),
  option: (provided, state) => ({
    ...provided,
    marginBottom: 6,
    borderRadius: 5,
    color: state.isSelected ? 'var(--text-dark)' : 'var(--text-primary)',
    background: state.isSelected ? 'var(--card-border)' : 'transparent',
    cursor: 'pointer',

    '&:hover': {
      background: 'var(--card-border)',
    },
  }),
}

function arrayMove<T>(array: readonly T[], from: number, to: number) {
  const slicedArray = array.slice()
  slicedArray.splice(
    to < 0 ? array.length + to : to,
    0,
    slicedArray.splice(from, 1)[0]
  )
  return slicedArray
}

const SortableMultiValue = SortableElement((props: MultiValueProps) => {
  // this prevents the menu from being opened/closed when the user clicks
  // on a value to begin dragging it. ideally, detecting a click (instead of
  // a drag) would still focus the control and toggle the menu, but that
  // requires some magic with refs that are out of scope for this example
  const onMouseDown: MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }
  const innerProps = { ...props.innerProps, onMouseDown }
  return <components.MultiValue {...props} innerProps={innerProps} />
})

const SortableMultiValueLabel = SortableHandle(
  (props: MultiValueGenericProps) => <components.MultiValueLabel {...props} />
)

const SortableMultiSelect = SortableContainer(Select)

export const SortableSelect = (props) => {
  const { styles: customStyles, selectProps } = props

  const [selected, setSelected] = useState([])
  const containerId = useMemo(
    () => `sort-select-${Math.random().toString(20).substr(2, 5)}`,
    []
  )

  useEffect(() => {
    if (props.defaultValue) {
      const initialSelected = []
      for (const value of props.defaultValue) {
        initialSelected.push(
          props.options.find((option) => option.value === value)
        )
      }
      setSelected(initialSelected)
    }
  }, [])

  const onChange = (newSelected) => {
    setSelected(newSelected)
    props.onChange?.(newSelected.map((option) => option.value).filter(Boolean))
  }

  const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
    const newSelected = arrayMove(selected, oldIndex, newIndex)
    setSelected(newSelected)
    props.onChange?.(newSelected.map((option) => option.value).filter(Boolean))
  }

  return (
    <div id={containerId} className="relative">
      <SortableMultiSelect
        useDragHandle
        // react-sortable-hoc props
        axis="xy"
        onSortEnd={onSortEnd}
        distance={4}
        lockToContainerEdges
        helperContainer={() => document.querySelector(`#${containerId}`)}
        // small fix for https://github.com/clauderic/react-sortable-hoc/pull/352:
        getHelperDimensions={({ node }) => node.getBoundingClientRect()}
        // react-select props
        value={selected}
        onChange={onChange}
        options={props.options}
        components={{
          MultiValue: SortableMultiValue,
          MultiValueLabel: SortableMultiValueLabel,
        }}
        isMulti
        closeMenuOnSelect={false}
        styles={{
          ...styles,
          ...(customStyles || {}),
        }}
        {...(selectProps || {})}
      />
    </div>
  )
}
